import {
  Box,
  Grid,
  Paper,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import Empty from '../../../components/common/Empty';
import { NumberPagination } from '../../../components/pagination/CustomPagination';
import { DetailedSearch } from '../../../components/search-input/DetailedSearchBar';
import { FacilitySelect } from '../../../components/select/SelectList';
import {
  PrimaryButton,
  TableButton,
} from '../../../components/style/ButtonStyle';
import {
  MaxWidthCell,
  PrettyScrollbarTableWrap,
  PrimaryBorderTable,
} from '../../../components/style/TableStyle';
import { FormTitle } from '../../../components/style/TextStyle';
import { useAuthAxios } from '../../../hooks/useAuthAxios';
import useResponsive from '../../../hooks/useResponsive';
import { useYupValidationResolver } from '../../../hooks/useYupValidationResolver';
import { AddButtonIcon } from '../../../layouts/dashboard/nav/IconButton';
import {
  OUTDOOR_GYM_EQUIPMENT_ID,
  SubCategoryTypeListLabel,
} from '../../../utils/enum';
import { fDate } from '../../../utils/formatTime';
import AddCategoryButton from './Common/AddCategoryButton';
import { CreateSportingForm } from './Common/CreateCategoryForm';
import DeleteCategoryButton from './Common/DeleteButton';
import EditCategoryButton from './Common/EditCategoryButton';
import { EditSportingForm } from './Common/EditCategoryForm';
import { sportingHeader } from './header';
import {
  sportingDefaultValue,
  sportingSchema,
} from './Common/DefaultValuesAndSchema';
const PAGE_SIZE = 10;

export default function SportingCategory() {
  const [search, setSearch] = useState('');
  const [subCategoryType, setSubCategoryType] = useState('');
  const mdUp = useResponsive('up', 'md');
  const [page, setPage] = useState(1);

  const navigate = useNavigate();

  const [getSportingsResult, getSportings] = useAuthAxios(
    {
      method: 'GET',
      url: `api/v1/subcategory`,
      params: {
        subCategoryType: subCategoryType,
        title: search,
        page: page,
        pageSize: PAGE_SIZE,
      },
    },
    { manual: false },
  );
  const sportings = _.get(getSportingsResult, 'response.data.result');
  const count = _.get(getSportingsResult, 'response.data.count', 0);
  const isEmpty = _.isEmpty(sportings);

  useEffect(() => {
    getSportings();
  }, [getSportings]);

  const resolver = useYupValidationResolver(
    Yup.object().shape({
      ...sportingSchema,
    }),
  );

  const form = useForm<any>({
    mode: 'onTouched',
    resolver,
    defaultValues: {
      ...sportingDefaultValue,
    },
    shouldUnregister: false,
  });
  const [postSportingResult, postSporting] = useAuthAxios({
    method: 'POST',
    url: '/api/v1/subcategory',
  });

  const onSubmit = form.handleSubmit(function (values) {
    const payload = {
      title: values.title,
      subCategoryType: values.subCategoryType,
      notes: values.notes,
      categoryId: OUTDOOR_GYM_EQUIPMENT_ID,
    };
    postSporting({ data: payload });
  });

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        {mdUp ? <FormTitle>운동기구 목록</FormTitle> : <Box />}
        {mdUp ? (
          <AddCategoryButton
            label="운동기구 추가"
            form={form}
            onSubmit={onSubmit}
            children={<CreateSportingForm form={form} />}
            postResult={postSportingResult}
            getResult={getSportings}
          />
        ) : (
          <Stack>
            <PrimaryButton
              onClick={() => navigate(`/category/sporting/form`)}
              startIcon={<AddButtonIcon />}
            >
              운동기구 추가
            </PrimaryButton>
            <Box />
          </Stack>
        )}
      </Stack>
      <Grid container>
        <Grid item xs={12} md={3} sx={{ pr: { md: 2 }, mb: { xs: 2, md: 0 } }}>
          <FacilitySelect
            value={subCategoryType}
            onChange={(e: any) => setSubCategoryType(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={9}>
          <DetailedSearch
            placeholder="운동기구명 검색"
            onKeyDown={e => {
              if (e.key === 'Enter') {
                setSearch(e.target.value);
              }
            }}
            onClick={(e, value) => {
              setSearch(value);
            }}
          />
        </Grid>
      </Grid>
      {isEmpty ? (
        <Empty />
      ) : (
        <PrettyScrollbarTableWrap>
          <TableContainer
            component={Paper}
            sx={{ mt: '16px !important', maxHeight: '412px' }}
          >
            <PrimaryBorderTable sx={{ minWidth: 910 }} size="small">
              <TableHead>
                <TableRow>
                  {_.map(sportingHeader, t => (
                    <TableCell align="center" key={t.id}>
                      {t?.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {_.map(sportings, sporting => {
                  const isDefault = _.get(sporting, 'title') === '기타';

                  return (
                    <TableRow key={sporting.subCategoryId}>
                      <TableCell align="center">
                        {sporting?.title || ''}
                      </TableCell>
                      <TableCell align="center">
                        {_.get(
                          SubCategoryTypeListLabel,
                          sporting?.subCategoryType,
                          '-',
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {fDate(sporting?.createdAt, 'yyyy-MM-dd') || '-'}
                      </TableCell>
                      <TableCell align="center">
                        {sporting?.createdBy || '-'}
                      </TableCell>
                      <MaxWidthCell align="center">
                        {sporting?.creatorDept || '-'}
                      </MaxWidthCell>
                      <MaxWidthCell align="center">
                        {sporting?.notes || '-'}
                      </MaxWidthCell>
                      <TableCell align="center">
                        {mdUp ? (
                          <EditCategoryButton
                            label="운동기구 수정"
                            form={form}
                            children={
                              <EditSportingForm
                                form={form}
                                subcategoryId={sporting?.subCategoryId}
                              />
                            }
                            category="subcategory"
                            categoryId={sporting?.subCategoryId}
                            getResult={getSportings}
                          />
                        ) : (
                          <TableButton
                            onClick={() => {
                              navigate(
                                `/category/sporting/${sporting?.subCategoryId}/form`,
                              );
                            }}
                          >
                            수정
                          </TableButton>
                        )}
                      </TableCell>

                      <TableCell align="center">
                        {isDefault ? (
                          <></>
                        ) : (
                          <DeleteCategoryButton
                            category="subcategory"
                            categoryId={sporting?.subCategoryId}
                            getResult={getSportings}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </PrimaryBorderTable>
          </TableContainer>
        </PrettyScrollbarTableWrap>
      )}
      <Stack direction="row" justifyContent="center">
        <NumberPagination
          boundaryCount={1}
          count={Math.ceil(count / PAGE_SIZE)}
          siblingCount={0}
          onChange={(e, page) => {
            setPage(page);
          }}
        />
      </Stack>
    </Stack>
  );
}
